import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button as AntButton, Modal, Table, TableColumnsType } from 'antd';
import useAxios from '../../hooks/useAxios';
import { IGetEntranceSchedules } from '../../models/placeTest.model';
import {
  changeEntranceSchedule,
  getEntranceSchedules,
} from '../../api/placeTest.api';
import config from '../../lib/config';
import Pagination from '../Pagination';
import moment from 'moment';
import { getDayOfWeek } from '../../lib/util';
import ErrorResponse from '../ErrorResponse';
import Loader from '../Loader';
import Button from '../Button';
import useNotification from '../../hooks/useNotification';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import SelectEduStep from '../SelectEduStep';
import { getCommonCodeLabel } from '../../lib/store';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  campusCode: string | undefined;
  platRequireCode: number | undefined;
  doGetExamineeDetail: (platRequireCode: number) => void;
  defaultEduStepCode: string | undefined;
  doGetEntranceDetail: () => void;
}

const SelectExamScheduleModal: FC<IProps> = ({
  visible,
  setVisible,
  campusCode,
  platRequireCode,
  doGetExamineeDetail,
  defaultEduStepCode,
  doGetEntranceDetail,
}) => {
  const { t, i18n } = useTranslation();
  const { serverDate } = useSelector((state: RootState) => state.serverDate);  
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetEntranceSchedules>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [page, setPage] = useState(1);
  const [data, setData] = useState<any>([]);
  const [eduStepCode, setEduStepCode] = useState(defaultEduStepCode);

  useEffect(() => {
    if (campusCode) {
      execute(getEntranceSchedules(campusCode));
    }
  }, [campusCode]);

  useEffect(() => {
    if (res && serverDate) {
      const _data: any = [];
      res.data?.forEach((item: any) => {
        if (moment(serverDate).subtract(1, 'days').isBefore(item.examDate)) {
          _data.push(item);
        }
      });
      setData(_data.reverse());
    }
  }, [res, serverDate]);

  useEffect(() => {
    if (submitRes) {
      if (platRequireCode) {
        doGetExamineeDetail(platRequireCode);
      }
      doGetEntranceDetail();
      successNotification(t('common.message.save1'));
      setVisible(false);
    }
  }, [submitRes]);

  const onSelect = (platScheduleCode: number) => {
    if (!platRequireCode) return;
    submitExecute(changeEntranceSchedule(platRequireCode, platScheduleCode));
  };

  const columns: TableColumnsType<any> = [
    {
      title: t('common.label.1'),
      dataIndex: 'eduStepCode',
      align: 'center',
      render: value => getCommonCodeLabel('PR0006', value, i18n.language),
    },
    {
      title: t('entrance.schedule.column1'),
      dataIndex: 'examDate',
      key: 'examDate',
      align: 'center',
      render: (value: string) =>
        `${moment(value).format('YYYY.MM.DD')} (${getDayOfWeek(value)})`,
    },
    {
      title: t('entrance.schedule.column2'),
      dataIndex: 'srtTime',
      key: 'srtTime',
      align: 'center',
    },
    {
      title: t('entrance.schedule.column3'),
      key: 'quota',
      dataIndex: 'quota',
      align: 'center',
    },
    {
      title: t('entrance.schedule.column4'),
      key: 'applyCnt',
      dataIndex: 'applyCnt',
      align: 'center',
    },
    {
      title: t('entrance.schedule.column5'),
      key: 'payCnt',
      dataIndex: 'payCnt',
      align: 'center',
    },
    {
      title: t('entrance.schedule.button2'),
      key: 'platScheduleCode',
      dataIndex: 'platScheduleCode',
      align: 'center',
      render: (value: number) => (
        <div className='comm-btn__area justify-content-center'>
          <Button
            className="btn-basic small pink"
            onClick={() => onSelect(value)}
            text={t('entrance.schedule.button2')}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <Modal
        title={t('entrance.schedule.button7')}
        width={750}
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={() => setVisible(false)}
            >
              {t('common.button.close')}
            </AntButton>
          </div>
        ]}
      >
        <div className='comm-bx__space'>
          <div className='comm-btn__area right'>
            <SelectEduStep
              eduStepCode={eduStepCode}
              setEduStepCode={value => {
                setEduStepCode(value);
                setPage(1);
              }}
              disabled={true}
            />   
          </div>
        </div>     
        <div className='comm-table__area mt-0'>
          <Table
            columns={columns}
            dataSource={data.filter((item: any) => item.eduStepCode === eduStepCode)}
            pagination={{
              current: page,
              pageSize: config.miniPageSize,
            }}
            rowKey="platScheduleCode"
          />
        </div>
        <div className='comm-bx__space'>
          <div className="common-paging center">
            <Pagination
              count={data.length}
              page={page}
              setPage={setPage}
              mini
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SelectExamScheduleModal;
